<div class="container mx-auto">
  <!-- Section Header -->
  <div
    class="flex flex-wrap items-center justify-between mb-6 pb-4 border-b border-base-300"
  >
    <div class="flex-1">
      <h2 class="text-2xl font-bold text-base-content flex items-center gap-2">
        <i class="fa-solid fa-users-rectangle text-primary"></i>
        Batch Management
      </h2>
      <p class="text-sm text-base-content/60 mt-1">
        Organize student cohorts, attach curricula (courses & live classes), and manage cohort enrollments
      </p>
    </div>
    <div class="flex items-center gap-2">
      @if (!selectedBatch) {
        <button
          type="button"
          class="daisy-btn daisy-btn-primary gap-2"
          (click)="openCreateBatchModal()"
        >
          <i class="fa-solid fa-plus"></i>
          <span class="hidden sm:inline">Create New Batch</span>
        </button>
      } @else {
        <button
          type="button"
          class="daisy-btn daisy-btn-outline gap-2"
          (click)="deselectBatch()"
        >
          <i class="fa-solid fa-arrow-left"></i>
          <span class="hidden sm:inline">Back to Batches</span>
        </button>
      }
    </div>
  </div>

  @if (selectedBatch) {
    <!-- Batch Detail View (Matches Instructor Live Class Detail View) -->
    <div class="bg-base-100 border border-base-300 rounded-2xl shadow-xl overflow-hidden mb-8">
      <!-- Header with gradient and back button -->
      <div class="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-8 text-white">
        <div class="flex items-center justify-between mb-4">
          <div class="daisy-breadcrumbs text-blue-100 text-sm">
            <ul>
              <li><span class="opacity-70">Workspace</span></li>
              <li><a (click)="deselectBatch()" class="hover:text-white transition-colors cursor-pointer">Batches</a></li>
              <li class="text-white font-medium">{{ selectedBatch.name }}</li>
            </ul>
          </div>
          <button
            type="button"
            (click)="deselectBatch()"
            class="daisy-btn daisy-btn-ghost daisy-btn-sm text-white hover:bg-white hover:bg-opacity-20 gap-2"
          >
            <i class="fa-solid fa-arrow-left"></i>
            <span class="hidden sm:inline">Back to Batches</span>
          </button>
        </div>
        <div class="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div class="flex items-center gap-2 mb-2">
              <span
                class="daisy-badge daisy-badge-sm font-semibold uppercase"
                [ngClass]="selectedBatch.status === 'ACTIVE' ? 'daisy-badge-success text-white' : 'daisy-badge-ghost text-white/80'"
              >
                {{ selectedBatch.status }}
              </span>
              @if (selectedBatch.startDate || selectedBatch.endDate) {
                <span class="text-xs text-blue-100">
                  <i class="fa-regular fa-calendar mr-1"></i>
                  {{ formatDate(selectedBatch.startDate) }} - {{ formatDate(selectedBatch.endDate) }}
                </span>
              }
            </div>
            <h2 class="text-3xl font-bold mb-2">{{ selectedBatch.name }}</h2>
            <p class="text-blue-100 text-base max-w-2xl">
              {{ selectedBatch.description || 'Manage curriculum, students, and cohort enrollments' }}
            </p>
          </div>

          <!-- Action buttons inside banner -->
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              (click)="openEditBatchModal(selectedBatch)"
              class="daisy-btn daisy-btn-sm bg-white text-primary hover:bg-blue-50 border-0 gap-2 shadow font-bold"
            >
              <i class="fa-solid fa-pen"></i>
              <span>Edit Batch</span>
            </button>
            <button
              type="button"
              (click)="deleteBatch(selectedBatch)"
              class="daisy-btn daisy-btn-sm daisy-btn-ghost text-white border border-white/30 hover:bg-error/30 gap-2"
            >
              <i class="fa-solid fa-trash"></i>
              <span>Delete Batch</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Navigation Tabs -->
      <div class="border-b border-base-300 px-6 pt-4 bg-base-100">
        <div class="daisy-tabs daisy-tabs-bordered">
          <button
            type="button"
            class="daisy-tab daisy-tab-lg gap-2"
            [class.daisy-tab-active]="activeDrawerTab === 'CURRICULUM'"
            (click)="activeDrawerTab = 'CURRICULUM'"
          >
            <i class="fa-solid fa-book-open"></i>
            Curriculum ({{ (selectedBatch.courses.length || 0) + (selectedBatch.liveClasses.length || 0) }})
          </button>
          <button
            type="button"
            class="daisy-tab daisy-tab-lg gap-2"
            [class.daisy-tab-active]="activeDrawerTab === 'STUDENTS'"
            (click)="activeDrawerTab = 'STUDENTS'"
          >
            <i class="fa-solid fa-user-graduate"></i>
            Students ({{ selectedBatch.students.length || 0 }})
          </button>
        </div>
      </div>

      <!-- Tab Content Area -->
      <div class="p-6">
        @if (activeDrawerTab === 'CURRICULUM') {
          <div class="space-y-8">
            <!-- Courses Section -->
            <div>
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-xl font-bold text-base-content flex items-center gap-2">
                    <i class="fa-solid fa-graduation-cap text-primary"></i>
                    Courses in Batch
                    <span class="text-sm font-normal text-base-content/60">({{ selectedBatch.courses.length || 0 }})</span>
                  </h3>
                  <p class="text-xs text-base-content/60 mt-0.5">Assigned asynchronous learning materials for this cohort</p>
                </div>
                <button
                  type="button"
                  (click)="openAddCourseModal()"
                  class="daisy-btn daisy-btn-sm daisy-btn-primary gap-2"
                >
                  <i class="fa-solid fa-plus"></i> Add Course
                </button>
              </div>

              @if (!selectedBatch.courses.length) {
                <div class="bg-base-200/40 border border-dashed border-base-300 rounded-2xl p-8 text-center text-sm text-base-content/60">
                  <i class="fa-solid fa-book-open text-2xl mb-2 opacity-50 block"></i>
                  No courses attached to this batch yet. Click "Add Course" to assign courses.
                </div>
              } @else {
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  @for (course of selectedBatch.courses; track course._id) {
                    <div class="daisy-card bg-base-100 border border-base-300 shadow-sm p-4 flex flex-col justify-between hover:shadow-md transition-all">
                      <div>
                        <div class="flex items-start justify-between gap-2 mb-2">
                          <h4 class="font-bold text-base text-base-content line-clamp-1">{{ course.title }}</h4>
                          <span
                            class="daisy-badge daisy-badge-sm shrink-0"
                            [ngClass]="course.settings?.isPublic !== false ? 'daisy-badge-info' : 'daisy-badge-warning'"
                          >
                            {{ course.settings?.isPublic !== false ? 'Public' : 'Unlisted' }}
                          </span>
                        </div>
                        <p class="text-xs text-base-content/60 mb-4 line-clamp-2">
                          {{ course.description || 'No description provided' }}
                        </p>
                      </div>

                      <div class="flex items-center justify-between pt-3 border-t border-base-200">
                        <button
                          type="button"
                          (click)="openBulkEnrollModal('COURSE', course)"
                          class="daisy-btn daisy-btn-xs daisy-btn-primary gap-1"
                          title="Enroll all students in this batch into this course"
                        >
                          <i class="fa-solid fa-user-plus"></i> Bulk Enroll
                        </button>
                        <button
                          type="button"
                          (click)="removeCourseFromBatch(course)"
                          class="daisy-btn daisy-btn-xs daisy-btn-ghost text-error hover:bg-error/10 gap-1"
                        >
                          <i class="fa-solid fa-xmark"></i> Remove
                        </button>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>

            <!-- Live Classes Section -->
            <div>
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-xl font-bold text-base-content flex items-center gap-2">
                    <i class="fa-solid fa-chalkboard-user text-accent"></i>
                    Live Classes in Batch
                    <span class="text-sm font-normal text-base-content/60">({{ selectedBatch.liveClasses.length || 0 }})</span>
                  </h3>
                  <p class="text-xs text-base-content/60 mt-0.5">Interactive live cohorts and scheduled lecture sessions</p>
                </div>
                <button
                  type="button"
                  (click)="openAddLiveClassModal()"
                  class="daisy-btn daisy-btn-sm daisy-btn-accent gap-2"
                >
                  <i class="fa-solid fa-plus"></i> Add Live Class
                </button>
              </div>

              @if (!selectedBatch.liveClasses.length) {
                <div class="bg-base-200/40 border border-dashed border-base-300 rounded-2xl p-8 text-center text-sm text-base-content/60">
                  <i class="fa-solid fa-chalkboard-user text-2xl mb-2 opacity-50 block"></i>
                  No live classes attached to this batch yet. Click "Add Live Class" to assign classes.
                </div>
              } @else {
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  @for (lc of selectedBatch.liveClasses; track (lc._id || lc.id)) {
                    <div class="daisy-card bg-base-100 border border-base-300 shadow-sm p-4 flex flex-col justify-between hover:shadow-md transition-all">
                      <div>
                        <div class="flex items-start justify-between gap-2 mb-2">
                          <h4 class="font-bold text-base text-base-content line-clamp-1">{{ lc.title }}</h4>
                          <span
                            class="daisy-badge daisy-badge-sm shrink-0"
                            [ngClass]="lc.settings?.isPublic !== false ? 'daisy-badge-info' : 'daisy-badge-warning'"
                          >
                            {{ lc.settings?.isPublic !== false ? 'Public' : 'Unlisted' }}
                          </span>
                        </div>
                        <p class="text-xs text-base-content/60 mb-4 line-clamp-2">
                          {{ lc.description || 'No description provided' }}
                        </p>
                      </div>

                      <div class="flex items-center justify-between pt-3 border-t border-base-200">
                        <button
                          type="button"
                          (click)="openBulkEnrollModal('LIVE_CLASS', lc)"
                          class="daisy-btn daisy-btn-xs daisy-btn-accent gap-1"
                          title="Enroll all students in this batch into this live class"
                        >
                          <i class="fa-solid fa-user-plus"></i> Bulk Enroll
                        </button>
                        <button
                          type="button"
                          (click)="removeLiveClassFromBatch(lc)"
                          class="daisy-btn daisy-btn-xs daisy-btn-ghost text-error hover:bg-error/10 gap-1"
                        >
                          <i class="fa-solid fa-xmark"></i> Remove
                        </button>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }

        @if (activeDrawerTab === 'STUDENTS') {
          <div>
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h3 class="text-xl font-bold text-base-content flex items-center gap-2">
                  <i class="fa-solid fa-user-graduate text-primary"></i>
                  Batch Roster
                  <span class="text-sm font-normal text-base-content/60">({{ selectedBatch.students.length || 0 }})</span>
                </h3>
                <p class="text-xs text-base-content/60 mt-0.5">Students assigned to this batch cohort</p>
              </div>
              <button
                type="button"
                (click)="openAddStudentModal()"
                class="daisy-btn daisy-btn-sm daisy-btn-primary gap-2"
              >
                <i class="fa-solid fa-user-plus"></i> Add Students
              </button>
            </div>

            @if (!selectedBatch.students.length) {
              <div class="bg-base-200/40 border border-dashed border-base-300 rounded-2xl p-12 text-center text-sm text-base-content/60">
                <i class="fa-solid fa-users text-3xl mb-3 opacity-50 block"></i>
                <p class="font-bold text-base-content text-base mb-1">No Students in Batch</p>
                <p>Click "Add Students" to enroll members from your academy into this batch.</p>
              </div>
            } @else {
              <!-- Roster Search -->
              <div class="mb-3">
                <div class="relative w-full sm:w-80">
                  <input
                    type="text"
                    [(ngModel)]="rosterSearch"
                    (ngModelChange)="onRosterSearchChange()"
                    placeholder="Search students in batch..."
                    class="daisy-input daisy-input-bordered daisy-input-sm w-full pl-9"
                  />
                  <i class="fa-solid fa-magnifying-glass absolute left-3 top-2.5 text-base-content/50 text-xs"></i>
                </div>
              </div>

              <!-- Roster Table -->
              <div class="overflow-x-auto border border-base-300 rounded-xl">
                <table class="daisy-table daisy-table-sm w-full">
                  <thead class="bg-base-200/60">
                    <tr>
                      <th class="text-xs font-bold text-base-content/70 uppercase">Student</th>
                      <th class="text-xs font-bold text-base-content/70 uppercase">Email / Username</th>
                      <th class="text-xs font-bold text-base-content/70 uppercase text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (student of pagedRosterStudents; track (student._id || student)) {
                      <tr class="hover:bg-base-200/40 transition-colors">
                        <td>
                          <div class="flex items-center gap-2.5">
                            <div class="w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0">
                              {{ getStudentInitial(student) }}
                            </div>
                            <span class="font-semibold text-sm text-base-content truncate max-w-[200px]">{{ getStudentName(student) }}</span>
                          </div>
                        </td>
                        <td class="text-xs text-base-content/60 truncate max-w-[200px]">{{ student?.email || student?.username }}</td>
                        <td class="text-right">
                          <button
                            type="button"
                            (click)="removeStudentFromBatch(student)"
                            class="daisy-btn daisy-btn-ghost daisy-btn-xs text-error hover:bg-error/10 gap-1"
                            title="Remove student from batch"
                          >
                            <i class="fa-solid fa-xmark text-sm"></i> Remove
                          </button>
                        </td>
                      </tr>
                    }
                    @if (!filteredRosterStudents.length) {
                      <tr>
                        <td colspan="3" class="text-center text-sm text-base-content/50 py-6">
                          No students match your search.
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              <!-- Roster Pagination -->
              @if (rosterTotalPages > 1) {
                <div class="flex items-center justify-between mt-3">
                  <span class="text-xs text-base-content/60">
                    Showing {{ ((rosterPage - 1) * rosterPageSize) + 1 }}–{{ rosterPage === rosterTotalPages ? filteredRosterStudents.length : rosterPage * rosterPageSize }} of {{ filteredRosterStudents.length }}
                  </span>
                  <div class="daisy-join">
                    <button
                      type="button"
                      class="daisy-join-item daisy-btn daisy-btn-xs"
                      [disabled]="rosterPage <= 1"
                      (click)="rosterPrevPage()"
                    >«</button>
                    <button class="daisy-join-item daisy-btn daisy-btn-xs daisy-btn-disabled">Page {{ rosterPage }} / {{ rosterTotalPages }}</button>
                    <button
                      type="button"
                      class="daisy-join-item daisy-btn daisy-btn-xs"
                      [disabled]="rosterPage >= rosterTotalPages"
                      (click)="rosterNextPage()"
                    >»</button>
                  </div>
                </div>
              }
            }
          </div>
        }
      </div>
    </div>
  } @else {
    <!-- Filters & Search Bar -->
    <div class="daisy-card bg-base-100 border border-base-300 p-4 mb-6 shadow-sm">
      <div class="flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div class="relative w-full sm:w-80">
          <input
            type="text"
            [(ngModel)]="search"
            (ngModelChange)="onSearchChange()"
            placeholder="Search batches by name..."
            class="daisy-input daisy-input-bordered daisy-input-sm w-full pl-9"
          />
          <i class="fa-solid fa-magnifying-glass absolute left-3 top-2.5 text-base-content/50 text-xs"></i>
        </div>

        <div class="flex items-center gap-2 w-full sm:w-auto">
          <select
            [(ngModel)]="statusFilter"
            (ngModelChange)="onStatusFilterChange()"
            class="daisy-select daisy-select-bordered daisy-select-sm"
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="ARCHIVED">Archived</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Batches Grid (Matches Instructor Live Classes Grid) -->
    @if (loading) {
      <div class="flex items-center justify-center py-20">
        <span class="daisy-loading daisy-loading-spinner daisy-loading-lg text-primary"></span>
      </div>
    } @else if (batches.length === 0) {
      <div class="text-center py-16">
        <div class="mb-8">
          <div class="w-24 h-24 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <i class="fa-solid fa-users-rectangle text-4xl text-base-content/40"></i>
          </div>
          <h3 class="text-2xl font-bold text-base-content mb-2">No Batches Yet</h3>
          <p class="text-base-content/70 max-w-md mx-auto">
            Get started by creating your first student batch. You'll be able to organize student cohorts, attach curricula, and coordinate enrollments.
          </p>
        </div>
        <button
          type="button"
          (click)="openCreateBatchModal()"
          class="daisy-btn daisy-btn-primary daisy-btn-lg gap-2 shadow-lg hover:shadow-xl transition-shadow"
        >
          <i class="fa-solid fa-plus"></i> Create New Batch
        </button>
      </div>
    } @else {
      <div class="space-y-8">
        <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          @for (batch of batches; track batch._id) {
            <div
              class="bg-base-100 border border-base-300 rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full"
            >
              <!-- Batch Banner (Gradient Header) -->
              <div
                class="relative h-32 overflow-hidden bg-gradient-to-br from-blue-600 to-purple-600 shrink-0 p-5 flex flex-col justify-between text-white"
              >
                <div class="flex items-center justify-between">
                  <span
                    class="daisy-badge daisy-badge-sm font-semibold uppercase text-xs"
                    [ngClass]="batch.status === 'ACTIVE' ? 'daisy-badge-success text-white' : 'daisy-badge-ghost text-white/80'"
                  >
                    {{ batch.status }}
                  </span>
                  <div class="flex items-center gap-1">
                    <button
                      type="button"
                      (click)="openEditBatchModal(batch, $event)"
                      class="daisy-btn daisy-btn-xs daisy-btn-circle daisy-btn-ghost text-white/80 hover:text-white hover:bg-white/20"
                      title="Edit Batch"
                    >
                      <i class="fa-solid fa-pen text-xs"></i>
                    </button>
                    <button
                      type="button"
                      (click)="deleteBatch(batch, $event)"
                      class="daisy-btn daisy-btn-xs daisy-btn-circle daisy-btn-ghost text-white/80 hover:text-white hover:bg-error/40"
                      title="Delete Batch"
                    >
                      <i class="fa-solid fa-trash text-xs"></i>
                    </button>
                  </div>
                </div>
                <div>
                  <h3 class="text-xl font-bold text-white line-clamp-1">
                    {{ batch.name }}
                  </h3>
                  @if (batch.startDate || batch.endDate) {
                    <p class="text-xs text-blue-100 mt-0.5">
                      <i class="fa-regular fa-calendar mr-1"></i>
                      {{ formatDate(batch.startDate) }} - {{ formatDate(batch.endDate) }}
                    </p>
                  }
                </div>
              </div>

              <!-- Batch Content -->
              <div class="p-6 flex-1 flex flex-col justify-between gap-4">
                <div>
                  @if (batch.description) {
                    <p class="text-sm text-base-content/70 line-clamp-2">{{ batch.description }}</p>
                  } @else {
                    <p class="text-sm text-base-content/40 italic">No description provided</p>
                  }
                </div>

                <!-- Metrics Pill Row -->
                <div class="grid grid-cols-3 gap-2 py-2.5 px-3 bg-base-200/50 rounded-xl text-center">
                  <div>
                    <div class="text-base font-bold text-primary">{{ batch.students.length || 0 }}</div>
                    <div class="text-[10px] font-semibold text-base-content/60 uppercase">Students</div>
                  </div>
                  <div class="border-x border-base-300">
                    <div class="text-base font-bold text-secondary">{{ batch.courses.length || 0 }}</div>
                    <div class="text-[10px] font-semibold text-base-content/60 uppercase">Courses</div>
                  </div>
                  <div>
                    <div class="text-base font-bold text-accent">{{ batch.liveClasses.length || 0 }}</div>
                    <div class="text-[10px] font-semibold text-base-content/60 uppercase">Classes</div>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="flex gap-3 pt-2">
                  <button
                    type="button"
                    (click)="selectBatch(batch)"
                    class="flex-1 daisy-btn daisy-btn-primary gap-2 shadow-md hover:shadow-lg transition-all"
                  >
                    <i class="fa-solid fa-cog"></i>
                    <span>Manage Batch</span>
                  </button>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    }
  }

  <!-- CREATE / EDIT BATCH MODAL -->
  @if (showBatchModal) {
    <div class="daisy-modal daisy-modal-open z-50">
      <div class="daisy-modal-box max-w-lg">
        <h3 class="font-bold text-lg text-base-content mb-4 flex items-center gap-2">
          <i class="fa-solid fa-users-rectangle text-primary"></i>
          {{ editingBatchId ? 'Edit Batch' : 'Create New Batch' }}
        </h3>

        <form (ngSubmit)="submitBatch()" class="space-y-4">
          <div>
            <label class="daisy-label py-1">
              <span class="daisy-label-text font-semibold">Batch Name *</span>
            </label>
            <input
              type="text"
              [(ngModel)]="batchForm.name"
              name="name"
              required
              placeholder="e.g. 2026 A/L Biology - Batch 01"
              class="daisy-input daisy-input-bordered w-full"
            />
          </div>

          <div>
            <label class="daisy-label py-1">
              <span class="daisy-label-text font-semibold">Description</span>
            </label>
            <textarea
              [(ngModel)]="batchForm.description"
              name="description"
              rows="3"
              placeholder="Optional notes or batch details..."
              class="daisy-textarea daisy-textarea-bordered w-full"
            ></textarea>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="daisy-label py-1">
                <span class="daisy-label-text font-semibold">Start Date</span>
              </label>
              <input
                type="date"
                [(ngModel)]="batchForm.startDate"
                name="startDate"
                class="daisy-input daisy-input-bordered w-full"
              />
            </div>
            <div>
              <label class="daisy-label py-1">
                <span class="daisy-label-text font-semibold">End Date</span>
              </label>
              <input
                type="date"
                [(ngModel)]="batchForm.endDate"
                name="endDate"
                class="daisy-input daisy-input-bordered w-full"
              />
            </div>
          </div>

          <div>
            <label class="daisy-label py-1">
              <span class="daisy-label-text font-semibold">Status</span>
            </label>
            <select
              [(ngModel)]="batchForm.status"
              name="status"
              class="daisy-select daisy-select-bordered w-full"
            >
              <option [value]="BatchStatus.ACTIVE">Active</option>
              <option [value]="BatchStatus.ARCHIVED">Archived</option>
            </select>
          </div>

          <div class="daisy-modal-action mt-6">
            <button
              type="button"
              (click)="closeBatchModal()"
              class="daisy-btn daisy-btn-ghost"
              [disabled]="submittingBatch()"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="daisy-btn daisy-btn-primary"
              [disabled]="submittingBatch() || !batchForm.name.trim()"
            >
              @if (submittingBatch()) {
                <span class="daisy-loading daisy-loading-spinner daisy-loading-sm"></span>
              }
              {{ editingBatchId ? 'Save Changes' : 'Create Batch' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  }

  <!-- ADD COURSE MODAL -->
  @if (showAddCourseModal) {
    <div class="daisy-modal daisy-modal-open z-50">
      <div class="daisy-modal-box max-w-md">
        <h3 class="font-bold text-lg text-base-content mb-4 flex items-center gap-2">
          <i class="fa-solid fa-graduation-cap text-primary"></i>
          Add Course to Batch
        </h3>

        @if (loadingCourses) {
          <div class="flex items-center justify-center py-8">
            <span class="daisy-loading daisy-loading-spinner text-primary"></span>
          </div>
        } @else if (!availableCourses.length) {
          <p class="text-sm text-base-content/60 py-4 text-center">
            No additional courses available to add.
          </p>
        } @else {
          <div class="space-y-4">
            <label class="daisy-label py-1">
              <span class="daisy-label-text font-semibold">Select Course</span>
            </label>
            <select
              [(ngModel)]="selectedCourseIdToAdd"
              class="daisy-select daisy-select-bordered w-full"
            >
              @for (c of availableCourses; track c._id) {
                <option [value]="c._id">
                  {{ c.title }} {{ c.settings?.isPublic === false ? '(Unlisted)' : '' }}
                </option>
              }
            </select>
          </div>
        }

        <div class="daisy-modal-action mt-6">
          <button
            type="button"
            (click)="closeAddCourseModal()"
            class="daisy-btn daisy-btn-ghost"
          >
            Cancel
          </button>
          <button
            type="button"
            (click)="confirmAddCourse()"
            class="daisy-btn daisy-btn-primary"
            [disabled]="!availableCourses.length || !selectedCourseIdToAdd"
          >
            Add Course
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ADD LIVE CLASS MODAL -->
  @if (showAddLiveClassModal) {
    <div class="daisy-modal daisy-modal-open z-50">
      <div class="daisy-modal-box max-w-md">
        <h3 class="font-bold text-lg text-base-content mb-4 flex items-center gap-2">
          <i class="fa-solid fa-chalkboard-user text-accent"></i>
          Add Live Class to Batch
        </h3>

        @if (loadingLiveClasses) {
          <div class="flex items-center justify-center py-8">
            <span class="daisy-loading daisy-loading-spinner text-accent"></span>
          </div>
        } @else if (!availableLiveClasses.length) {
          <p class="text-sm text-base-content/60 py-4 text-center">
            No additional live classes available to add.
          </p>
        } @else {
          <div class="space-y-4">
            <label class="daisy-label py-1">
              <span class="daisy-label-text font-semibold">Select Live Class</span>
            </label>
            <select
              [(ngModel)]="selectedLiveClassIdToAdd"
              class="daisy-select daisy-select-bordered w-full"
            >
              @for (lc of availableLiveClasses; track (lc._id || lc.id)) {
                <option [value]="lc._id || lc.id">
                  {{ lc.title }} {{ lc.settings?.isPublic === false ? '(Unlisted)' : '' }}
                </option>
              }
            </select>
          </div>
        }

        <div class="daisy-modal-action mt-6">
          <button
            type="button"
            (click)="closeAddLiveClassModal()"
            class="daisy-btn daisy-btn-ghost"
          >
            Cancel
          </button>
          <button
            type="button"
            (click)="confirmAddLiveClass()"
            class="daisy-btn daisy-btn-accent"
            [disabled]="!availableLiveClasses.length || !selectedLiveClassIdToAdd"
          >
            Add Live Class
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ADD STUDENTS MODAL -->
  @if (showAddStudentModal) {
    <div class="daisy-modal daisy-modal-open z-50">
      <div class="daisy-modal-box max-w-lg">
        <h3 class="font-bold text-lg text-base-content mb-2 flex items-center gap-2">
          <i class="fa-solid fa-user-plus text-primary"></i>
          Add Students to Batch
        </h3>
        <p class="text-xs text-base-content/60 mb-4">
          Select students from your academy to include in {{ selectedBatch?.name }}.
          @if (studentTotal) {
            <span class="font-semibold">({{ studentTotal }} total students)</span>
          }
        </p>

        <!-- Debounced Search -->
        <div class="mb-3 relative">
          <input
            type="text"
            [(ngModel)]="studentSearch"
            (ngModelChange)="onStudentSearchChange()"
            placeholder="Search by name or email..."
            class="daisy-input daisy-input-bordered daisy-input-sm w-full pl-9"
          />
          <i class="fa-solid fa-magnifying-glass absolute left-3 top-2.5 text-base-content/50 text-xs"></i>
        </div>

        @if (loadingStudents) {
          <div class="flex items-center justify-center py-8">
            <span class="daisy-loading daisy-loading-spinner text-primary"></span>
          </div>
        } @else if (!tenantStudents.length) {
          <p class="text-sm text-base-content/60 py-4 text-center">
            No matching students available to add.
          </p>
        } @else {
          <!-- Select All Header -->
          <div class="flex items-center justify-between mb-2 px-2">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                [checked]="areAllVisibleSelected()"
                (change)="toggleSelectAllStudents()"
                class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-xs"
              />
              <span class="text-xs font-semibold text-base-content/70">Select all on this page</span>
            </label>
            <span class="text-xs text-base-content/50">Page {{ studentPage }} / {{ studentTotalPages }}</span>
          </div>

          <div class="max-h-72 overflow-y-auto space-y-1 pr-1 border border-base-200 rounded-lg p-2">
            @for (student of tenantStudents; track student._id) {
              <label
                class="flex items-center justify-between p-2 rounded-lg hover:bg-base-200/60 cursor-pointer transition-colors"
                [class.bg-primary/10]="selectedStudentIdsToAdd.has(student._id)"
              >
                <div class="flex items-center gap-2.5 min-w-0">
                  <input
                    type="checkbox"
                    [checked]="selectedStudentIdsToAdd.has(student._id)"
                    (change)="toggleStudentSelection(student._id)"
                    class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-sm"
                  />
                  <div class="min-w-0">
                    <span class="font-semibold text-xs text-base-content block truncate">{{ getStudentName(student) }}</span>
                    <span class="text-[11px] text-base-content/60 block truncate">{{ student.email || student.username }}</span>
                  </div>
                </div>
              </label>
            }
          </div>

          <!-- Server-side Pagination -->
          @if (studentTotalPages > 1) {
            <div class="flex items-center justify-center gap-2 mt-3">
              <button
                type="button"
                class="daisy-btn daisy-btn-xs daisy-btn-outline"
                [disabled]="studentPage <= 1"
                (click)="studentPrevPage()"
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>
              <span class="text-xs text-base-content/60">Page {{ studentPage }} of {{ studentTotalPages }}</span>
              <button
                type="button"
                class="daisy-btn daisy-btn-xs daisy-btn-outline"
                [disabled]="studentPage >= studentTotalPages"
                (click)="studentNextPage()"
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          }
        }

        <div class="daisy-modal-action mt-6 flex items-center justify-between">
          <span class="text-xs text-base-content/60">
            {{ selectedStudentIdsToAdd.size }} student(s) selected
          </span>
          <div class="flex items-center gap-2">
            <button
              type="button"
              (click)="closeAddStudentModal()"
              class="daisy-btn daisy-btn-ghost"
            >
              Cancel
            </button>
            <button
              type="button"
              (click)="confirmAddStudents()"
              class="daisy-btn daisy-btn-primary"
              [disabled]="!selectedStudentIdsToAdd.size"
            >
              Add Selected Students
            </button>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- BULK ENROLL MODAL -->
  @if (showBulkEnrollModal) {
    <div class="daisy-modal daisy-modal-open z-50">
      <div class="daisy-modal-box max-w-md">
        <h3 class="font-bold text-lg text-base-content mb-2 flex items-center gap-2">
          <i class="fa-solid fa-users-gear text-primary"></i>
          Bulk Enroll Batch Students
        </h3>
        <p class="text-xs text-base-content/70 mb-4">
          This will grant active access to all <strong>{{ selectedBatch?.students?.length || 0 }}</strong> students in <strong>{{ selectedBatch?.name }}</strong> for:
        </p>

        <div class="bg-base-200/50 p-3 rounded-lg mb-4 flex items-center gap-3">
          <i
            class="text-lg"
            [ngClass]="bulkTargetType === 'COURSE' ? 'fa-solid fa-graduation-cap text-primary' : 'fa-solid fa-chalkboard-user text-accent'"
          ></i>
          <div>
            <span class="text-xs font-semibold uppercase text-base-content/60">{{ bulkTargetType === 'COURSE' ? 'Course' : 'Live Class' }}</span>
            <h4 class="font-bold text-sm text-base-content">{{ bulkTargetTitle }}</h4>
          </div>
        </div>

        @if (bulkTargetType === 'COURSE') {
          <div class="mb-4">
            <label class="daisy-label py-1">
              <span class="daisy-label-text font-semibold">Access Duration (Days)</span>
            </label>
            <input
              type="number"
              [(ngModel)]="bulkExpiresInDays"
              min="1"
              max="1825"
              class="daisy-input daisy-input-bordered w-full"
            />
            <span class="text-xs text-base-content/60 mt-1 block">Default: 365 days (1 year)</span>
          </div>
        }

        <div class="daisy-alert daisy-alert-info text-xs py-2 px-3">
          <i class="fa-solid fa-circle-info"></i>
          <span>Students who are already actively enrolled will be skipped automatically.</span>
        </div>

        <div class="daisy-modal-action mt-6">
          <button
            type="button"
            (click)="closeBulkEnrollModal()"
            class="daisy-btn daisy-btn-ghost"
            [disabled]="submittingBulkEnroll()"
          >
            Cancel
          </button>
          <button
            type="button"
            (click)="confirmBulkEnroll()"
            class="daisy-btn daisy-btn-primary"
            [disabled]="submittingBulkEnroll()"
          >
            @if (submittingBulkEnroll()) {
              <span class="daisy-loading daisy-loading-spinner daisy-loading-sm"></span>
            }
            Confirm & Enroll Students
          </button>
        </div>
      </div>
    </div>
  }
</div>
